import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowRight, MapPin, Layers, Clock, Trash2, ScanSearch,
  Wrench, IndianRupee, ShieldCheck, Factory, GitCompare, Check, Boxes, Tag, X,
} from "lucide-react";
import { Header } from "@/components/Header";
import { UploadZone } from "@/components/UploadZone";
import { AnalyzingOverlay } from "@/components/AnalyzingOverlay";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { analyzePart, fetchHistory, deleteAnalysis, listRates, setRetention, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HERO_BG =
  "https://images.unsplash.com/photo-1769152683420-f4eff91cb30b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxlbmdpbmVlcmluZyUyMGJsdWVwcmludCUyMENBRHxlbnwwfHx8fDE3OTA2Nzk5OTd8MA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: ScanSearch, title: "GD&T Extraction", desc: "Reads tolerances, datums, material & finish callouts straight from the PDF." },
  { icon: Factory, title: "Process Routing", desc: "Casting, forging, stamping, welding, moulding or machining — single or multi-process." },
  { icon: Wrench, title: "Tooling & Fixtures", desc: "Suggested tooling, fixtures and machinery to actually make the part." },
  { icon: IndianRupee, title: "Costing by Pincode", desc: "Realistic INR cost ranges tuned to your Indian region and quantity." },
];

export default function Home() {
  const navigate = useNavigate();
  const { user, setShowLogin, setShowUpgrade, refreshUsage } = useAuth();
  const [pdf, setPdf] = useState(null);
  const [model3d, setModel3d] = useState(null);
  const [pincode, setPincode] = useState("");
  const [quantity, setQuantity] = useState(12000);
  const [batchOverride, setBatchOverride] = useState("");
  const [partHint, setPartHint] = useState("");
  const [rates, setRates] = useState([]);
  const [appliedRate, setAppliedRate] = useState(null);
  const [rateDismissed, setRateDismissed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(new Set());

  const MAX_PDF = 20 * 1024 * 1024;
  const MAX_3D = 40 * 1024 * 1024;

  const handlePdf = (f) => {
    if (f && !/\.pdf$/i.test(f.name)) return toast.error("Please select a PDF file for the 2D drawing.");
    if (f && f.size > MAX_PDF) return toast.error("PDF is too large (max 20 MB). Please compress or re-export it.");
    setPdf(f);
  };
  const handle3d = (f) => {
    if (f && !/\.(step|stp|iges|igs)$/i.test(f.name)) return toast.error("3D file must be .step, .stp, .iges or .igs.");
    if (f && f.size > MAX_3D) return toast.error("3D file is too large (max 40 MB).");
    setModel3d(f);
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const loadHistory = async () => {
    try {
      setHistory(await fetchHistory());
    } catch (e) {
      /* silent */
    }
  };

  useEffect(() => { loadHistory(); }, [user]);

  const handleRetention = async (e) => {
    const days = Number(e.target.value);
    try {
      await setRetention(days);
      await refreshUsage();
      toast.success(days === 0 ? "Auto-delete turned off." : `Drawings & analyses will auto-delete after ${days} days.`);
    } catch {
      toast.error("Could not update retention setting.");
    }
  };

  useEffect(() => {
    if (user) listRates().then(setRates).catch(() => setRates([]));
    else setRates([]);
  }, [user]);

  // Auto-apply a saved rate when its pincode matches (until the user dismisses it)
  useEffect(() => {
    if (rateDismissed) return;
    if (!/^\d{6}$/.test(pincode)) { setAppliedRate(null); return; }
    const match = rates.find((r) => r.pincode === pincode);
    if (match) setAppliedRate(match);
    else if (appliedRate && appliedRate.pincode) setAppliedRate(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pincode, rates, rateDismissed]);

  const handleAnalyze = async () => {
    if (!pdf && !model3d) return toast.error("Upload a 2D PDF drawing or a 3D file (STEP/IGES) to begin.");
    if (!/^\d{6}$/.test(pincode)) return toast.error("Enter a valid 6-digit Indian pincode.");
    if (!user) {
      toast.info("Sign in to run an estimate — you get 3 free.");
      setShowLogin(true);
      return;
    }
    if (!user.is_owner && !user.unlimited && user.remaining <= 0) {
      setShowUpgrade(true);
      return;
    }
    setLoading(true);
    const fd = new FormData();
    if (pdf) fd.append("pdf", pdf);
    if (model3d) fd.append("model_3d", model3d);
    fd.append("pincode", pincode);
    fd.append("quantity", String(quantity || 1));
    if (batchOverride) fd.append("batch_override", String(batchOverride));
    fd.append("part_hint", partHint);
    if (appliedRate) {
      fd.append("rate_override", JSON.stringify({
        material: appliedRate.material,
        material_rate_inr_per_kg: appliedRate.material_rate_inr_per_kg,
        labour_rate_inr_per_hr: appliedRate.labour_rate_inr_per_hr,
        machine_rate_inr_per_hr: appliedRate.machine_rate_inr_per_hr,
      }));
    }
    try {
      const data = await analyzePart(fd);
      await refreshUsage();
      navigate(`/analysis/${data.id}`);
    } catch (e) {
      setLoading(false);
      if (e?.response?.status === 402) {
        setShowUpgrade(true);
        return;
      }
      if (e?.response?.status === 401) {
        toast.error("Please sign in again.");
        setShowLogin(true);
        return;
      }
      toast.error(e?.response?.data?.detail || "Could not start analysis. Please try again.");
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await deleteAnalysis(id);
      setHistory((h) => h.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {loading && <AnalyzingOverlay />}
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="blueprint-grid absolute inset-0 opacity-50" />
        <img src={HERO_BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.06]" />
        <div className="noise-overlay absolute inset-0 opacity-[0.15]" />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-5 py-14 sm:px-8 lg:grid-cols-[1.1fr_1fr] lg:py-20">
          {/* Left: copy + upload */}
          <div>
            <div className="mb-5 inline-flex items-center gap-2 rounded-sm border border-border bg-card px-3 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-success" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Cost to Manufacture · AI
              </span>
            </div>
            <h1 className="font-display text-4xl font-black leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
              From 2D drawings or 3D files to <span className="text-primary">manufacturing plan</span> & cost.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
              Upload an engineering PDF (and optional 3D STEP/IGES). Our AI extracts GD&T, material,
              heat & surface treatment, then recommends the process, tooling, fixtures, machinery and a
              realistic INR cost for your Indian pincode.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-8 rounded-sm border border-border bg-card p-5 shadow-sm sm:p-6"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <UploadZone
                  testid="upload-pdf"
                  label="2D Drawing (PDF)"
                  hint="PDF or a 3D file · drag & drop or click"
                  accept="application/pdf,.pdf"
                  icon="pdf"
                  file={pdf}
                  onFile={handlePdf}
                />
                <UploadZone
                  testid="upload-3d"
                  label="3D Model (STEP / IGES)"
                  hint="Use alone or with the PDF · .step .stp .iges .igs"
                  accept=".step,.stp,.iges,.igs"
                  icon="box"
                  file={model3d}
                  onFile={handle3d}
                />
              </div>

              {model3d && !pdf && (
                <p className="mt-2 font-mono text-[11px] text-muted-foreground" data-testid="threed-only-hint">
                  No 2D PDF — the AI will estimate from the 3D file. Add your raw material &amp; key
                  requirements in the note below for a sharper estimate.
                </p>
              )}

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="pincode" className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                    Manufacturing Pincode
                  </Label>
                  <div className="relative mt-1.5">
                    <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="pincode"
                      data-testid="pincode-input"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="e.g. 411019"
                      value={pincode}
                      onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
                      className="pl-9 font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="qty" className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                    Annual Requirement
                  </Label>
                  <div className="relative mt-1.5">
                    <Layers className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="qty"
                      data-testid="quantity-input"
                      inputMode="numeric"
                      placeholder="e.g. 12000 / year"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value.replace(/\D/g, ""))}
                      className="pl-9 font-mono"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-3">
                <Label htmlFor="batch" className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                  Batch / MOQ override <span className="normal-case tracking-normal text-muted-foreground/70">(optional)</span>
                </Label>
                <div className="relative mt-1.5">
                  <Boxes className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="batch"
                    data-testid="batch-override-input"
                    inputMode="numeric"
                    placeholder="Leave blank = auto (annual ÷ 12)"
                    value={batchOverride}
                    onChange={(e) => setBatchOverride(e.target.value.replace(/\D/g, ""))}
                    className="pl-9 font-mono"
                  />
                </div>
              </div>

              <Input
                data-testid="part-hint-input"
                placeholder={model3d && !pdf
                  ? "Raw material + key requirements (e.g. EN8 flat bar, ±0.1 tolerance, zinc plating)…"
                  : "Optional note (e.g. gearbox bracket, target EN8 material)…"}
                value={partHint}
                onChange={(e) => setPartHint(e.target.value)}
                className="mt-4"
              />

              {user && rates.length > 0 && (
                <div className="mt-4" data-testid="rate-library-block">
                  {appliedRate ? (
                    <div className="flex flex-wrap items-center gap-2 rounded-sm border border-primary/40 bg-primary/[0.05] px-3 py-2" data-testid="applied-rate-chip">
                      <Tag className="h-3.5 w-3.5 text-primary" />
                      <span className="font-mono text-xs">
                        Your rates{appliedRate.material ? ` · ${appliedRate.material}` : ""}
                        {appliedRate.pincode ? ` · ${appliedRate.pincode}` : ""}:
                        {appliedRate.material_rate_inr_per_kg ? ` ₹${appliedRate.material_rate_inr_per_kg}/kg` : ""}
                        {appliedRate.labour_rate_inr_per_hr ? ` · ₹${appliedRate.labour_rate_inr_per_hr}/hr lab` : ""}
                        {appliedRate.machine_rate_inr_per_hr ? ` · ₹${appliedRate.machine_rate_inr_per_hr}/hr mc` : ""}
                      </span>
                      <button
                        data-testid="remove-applied-rate"
                        onClick={() => { setAppliedRate(null); setRateDismissed(true); }}
                        className="ml-auto text-muted-foreground hover:text-destructive"
                        title="Don't apply my saved rates"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <select
                      data-testid="rate-select"
                      value=""
                      onChange={(e) => {
                        const r = rates.find((x) => x.id === e.target.value);
                        if (r) { setAppliedRate(r); setRateDismissed(false); }
                      }}
                      className="w-full rounded-sm border border-border bg-card px-3 py-2 font-mono text-xs text-muted-foreground"
                    >
                      <option value="">Apply my saved supplier rates (optional)…</option>
                      {rates.map((r) => (
                        <option key={r.id} value={r.id}>
                          {[r.material, r.region, r.pincode].filter(Boolean).join(" · ")}
                          {r.material_rate_inr_per_kg ? ` — ₹${r.material_rate_inr_per_kg}/kg` : ""}
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    data-testid="manage-rates-link"
                    onClick={() => navigate("/rates")}
                    className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground hover:text-primary"
                  >
                    Manage rate library →
                  </button>
                </div>
              )}
              {user && rates.length === 0 && (
                <button
                  data-testid="add-rates-link"
                  onClick={() => navigate("/rates")}
                  className="mt-3 font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground hover:text-primary"
                >
                  + Add your supplier rates
                </button>
              )}

              <Button
                data-testid="analyze-button"
                onClick={handleAnalyze}
                disabled={loading}
                className="mt-5 h-12 w-full rounded-sm text-sm font-semibold tracking-wide"
              >
                Analyse & Estimate Cost
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </motion.div>
          </div>

          {/* Right: feature bento */}
          <div className="grid content-start gap-4 sm:grid-cols-2">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
                  className="rounded-sm border border-border bg-card/80 p-5 backdrop-blur-sm transition-transform duration-200 hover:-translate-y-0.5"
                >
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-sm bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-title text-base font-semibold tracking-tight">{f.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{f.desc}</p>
                </motion.div>
              );
            })}
            <div className="rounded-sm border border-border bg-primary p-5 text-primary-foreground sm:col-span-2">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                <span className="font-title font-semibold">India Cost. Global Reach.</span>
              </div>
              <p className="mt-1.5 text-sm text-primary-foreground/80">
                Estimates reflect Indian regional labour, material clusters and logistics so you can plan
                sourcing with confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HISTORY */}
      <section className="mx-auto max-w-7xl px-5 py-12 sm:px-8">
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <Clock className="h-5 w-5 text-muted-foreground" />
          <h2 className="font-display text-2xl font-bold tracking-tight">Recent analyses</h2>
          <span className="font-mono text-xs text-muted-foreground">({history.length})</span>
          {user && (
            <div className="ml-auto flex items-center gap-2" data-testid="retention-control">
              <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
              <label className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">Auto-delete after</label>
              <select
                data-testid="retention-select"
                value={String(user.retention_days ?? 0)}
                onChange={handleRetention}
                className="rounded-sm border border-border bg-card px-2 py-1 font-mono text-xs"
              >
                <option value="0">Off (keep forever)</option>
                <option value="7">7 days</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
                <option value="180">180 days</option>
                <option value="365">365 days</option>
              </select>
            </div>
          )}
        </div>
        {user && history.length >= 2 && (
          <div className="mb-4 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
            <GitCompare className="h-3.5 w-3.5" /> Select 2+ to compare
          </div>
        )}

        {history.length === 0 ? (
          <div
            data-testid="history-empty"
            className="rounded-sm border border-dashed border-border p-12 text-center"
          >
            <p className="text-sm text-muted-foreground">
              No analyses yet. Upload a drawing above to create your first manufacturing report.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {history.map((h) => (
              <div
                key={h.id}
                data-testid={`history-card-${h.id}`}
                onClick={() => navigate(`/analysis/${h.id}`)}
                className={`group cursor-pointer rounded-sm border bg-card p-5 transition-transform duration-200 hover:-translate-y-0.5 hover:border-primary/50 ${
                  selected.has(h.id) ? "border-primary ring-1 ring-primary" : "border-border"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-start gap-2.5">
                    <button
                      data-testid={`history-select-${h.id}`}
                      onClick={(e) => { e.stopPropagation(); toggleSelect(h.id); }}
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border transition-colors ${
                        selected.has(h.id) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"
                      }`}
                    >
                      {selected.has(h.id) && <Check className="h-3.5 w-3.5" />}
                    </button>
                    <h3 className="font-title text-base font-semibold leading-snug tracking-tight">
                      {h.part_name}
                    </h3>
                  </div>
                  <button
                    data-testid={`history-delete-${h.id}`}
                    onClick={(e) => handleDelete(e, h.id)}
                    className="shrink-0 rounded-sm p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {h.primary_process && (
                    <span className="rounded-sm bg-secondary px-2 py-0.5 font-mono text-[11px] uppercase tracking-wide text-secondary-foreground">
                      {h.primary_process}
                    </span>
                  )}
                  <span className="rounded-sm bg-secondary px-2 py-0.5 font-mono text-[11px] text-secondary-foreground">
                    {h.region || h.pincode}
                  </span>
                </div>
                <div className="mt-4 flex items-end justify-between border-t border-border pt-3">
                  <div>
                    <div className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">Per unit</div>
                    {h.status === "processing" ? (
                      <div className="font-mono text-sm font-semibold text-chart-3">Analysing…</div>
                    ) : h.status === "error" ? (
                      <div className="font-mono text-sm font-semibold text-destructive">Failed</div>
                    ) : (
                      <div className="font-mono text-lg font-semibold text-primary">
                        {inr(h.per_unit_min)}–{inr(h.per_unit_max)}
                      </div>
                    )}
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {selected.size >= 2 && (
        <div
          data-testid="compare-bar"
          className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-sm border border-border bg-card/95 px-4 py-3 shadow-lg backdrop-blur-xl"
        >
          <span className="font-mono text-sm">{selected.size} selected</span>
          <Button
            data-testid="compare-button"
            onClick={() => navigate(`/compare?ids=${[...selected].join(",")}`)}
            className="rounded-sm"
          >
            <GitCompare className="mr-2 h-4 w-4" /> Compare
          </Button>
          <button
            data-testid="compare-clear"
            onClick={() => setSelected(new Set())}
            className="font-mono text-xs uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
